import { useCallback, useEffect, useState } from 'react';
import { useDaily, useParticipantIds, useLocalSessionId, useDailyEvent } from '@daily-co/daily-react';
import type { DailyCall } from '@daily-co/daily-js';
import { notifications } from '@mantine/notifications';
import { IconUserPlus, IconUserMinus, IconAlertCircle, IconPlugConnected } from '@tabler/icons-react';

export const useVideoCall = () => {
  const callObject = useDaily();
  const participantIds = useParticipantIds();
  const localSessionId = useLocalSessionId();
  const [isJoined, setIsJoined] = useState(false);
  const [isCameraOn, setIsCameraOn] = useState(true);
  const [isMicOn, setIsMicOn] = useState(true);

  // Event listeners com notificações elegantes
  useDailyEvent('joined-meeting', useCallback(() => {
    console.log('✅ Joined meeting');
    setIsJoined(true);
    notifications.show({
      title: 'Conectado',
      message: 'Você entrou na sala',
      color: 'green',
      icon: <IconPlugConnected size={18} />,
      autoClose: 3000,
    });
  }, []));

  useDailyEvent('left-meeting', useCallback(() => {
    console.log('👋 Left meeting');
    setIsJoined(false);
  }, []));

  useDailyEvent('error', useCallback((event: any) => {
    console.error('❌ Daily error:', event);
    notifications.show({
      title: 'Erro de conexão',
      message: event.errorMsg || 'Ocorreu um erro na conferência',
      color: 'red',
      icon: <IconAlertCircle size={18} />,
      autoClose: 5000,
    });
  }, []));

  useDailyEvent('participant-joined', useCallback((event: any) => {
    const participantName = event.participant?.user_name || 'Alguém';
    console.log('👤 Participant joined:', participantName);

    // Não mostrar notificação para o próprio usuário
    if (!event.participant?.local) {
      notifications.show({
        title: 'Participante entrou',
        message: `${participantName} entrou na sala`,
        color: 'blue',
        icon: <IconUserPlus size={18} />,
        autoClose: 3000,
      });
    }
  }, []));

  useDailyEvent('participant-left', useCallback((event: any) => {
    const participantName = event.participant?.user_name || 'Alguém';
    console.log('👋 Participant left:', participantName);

    notifications.show({
      title: 'Participante saiu',
      message: `${participantName} saiu da sala`,
      color: 'gray',
      icon: <IconUserMinus size={18} />,
      autoClose: 3000,
    });
  }, []));

  // Join call
  const joinCall = useCallback(async (roomUrl: string, userName: string) => {
    if (!callObject) return;

    try {
      console.log('🚀 Joining call:', roomUrl);

      // Extract token from URL if present
      const url = new URL(roomUrl);
      const token = url.searchParams.get('t');
      const cleanUrl = roomUrl.split('?')[0]; // URL without query params

      console.log('🔑 Token extracted:', token ? 'Yes' : 'No');

      await callObject.join({
        url: cleanUrl,
        userName,
        ...(token && { token }), // Pass token explicitly if present
      });
    } catch (error) {
      console.error('Error joining call:', error);
      throw error;
    }
  }, [callObject]);

  // Leave call
  const leaveCall = useCallback(async () => {
    if (!callObject) return;

    try {
      console.log('👋 Leaving call');
      await callObject.leave();
    } catch (error) {
      console.error('Error leaving call:', error);
      throw error;
    }
  }, [callObject]);

  // Toggle camera
  const toggleCamera = useCallback(() => {
    if (!callObject) return;

    const isVideoEnabled = callObject.localVideo();
    callObject.setLocalVideo(!isVideoEnabled);
    setIsCameraOn(!isVideoEnabled);
  }, [callObject]);

  // Toggle microphone
  const toggleMicrophone = useCallback(() => {
    if (!callObject) return;

    const isAudioEnabled = callObject.localAudio();
    callObject.setLocalAudio(!isAudioEnabled);
    setIsMicOn(!isAudioEnabled);
  }, [callObject]);

  // Update camera/mic state when they change
  useEffect(() => {
    if (!callObject) return;

    const updateDeviceState = () => {
      setIsCameraOn(callObject.localVideo());
      setIsMicOn(callObject.localAudio());
    };

    callObject.on('camera-error', updateDeviceState);
    // @ts-ignore - Daily.co event types may not include all events
    callObject.on('local-video-updated', updateDeviceState);
    // @ts-ignore - Daily.co event types may not include all events
    callObject.on('local-audio-updated', updateDeviceState);

    return () => {
      callObject.off('camera-error', updateDeviceState);
      // @ts-ignore
      callObject.off('local-video-updated', updateDeviceState);
      // @ts-ignore
      callObject.off('local-audio-updated', updateDeviceState);
    };
  }, [callObject]);

  return {
    callObject,
    participantIds,
    localSessionId,
    isJoined,
    isCameraOn,
    isMicOn,
    joinCall,
    leaveCall,
    toggleCamera,
    toggleMicrophone,
  };
};
